import { useState, useMemo, useRef, useEffect } from 'react';
import { PageType } from '@/types/sitemap';
import { Search, X } from 'lucide-react';

const pageTemplates: { type: PageType; label: string }[] = [
  { type: 'home', label: 'Homepage' },
  { type: 'landing', label: 'Landing Page' },
  { type: 'blog', label: 'Blog' },
  { type: 'blog-archive', label: 'Blog Archive' },
  { type: 'blog-single', label: 'Blog Post' },
  { type: 'about', label: 'About' },
  { type: 'contact', label: 'Contact' },
  { type: 'pricing', label: 'Pricing' },
  { type: 'product', label: 'Product' },
  { type: 'login', label: 'Login' },
  { type: 'signup', label: 'Sign Up' },
  { type: 'dashboard', label: 'Dashboard' },
  { type: 'profile', label: 'Profile' },
  { type: 'settings', label: 'Settings' },
  { type: 'portfolio', label: 'Portfolio' },
  { type: 'services', label: 'Services' },
  { type: 'careers', label: 'Careers' },
  { type: 'faq', label: 'FAQ' },
  { type: 'terms', label: 'Terms & Conditions' },
  { type: 'privacy', label: 'Privacy Policy' },
  { type: '404', label: '404 Page' },
  { type: 'default', label: 'Custom Page' },
];

interface Props {
  onSelect: (type: PageType, name: string) => void;
  onClose: () => void;
  position: { x: number; y: number };
}

export function AddPagePopover({ onSelect, onClose, position }: Props) {
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search) return pageTemplates;
    return pageTemplates.filter(p => p.label.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  useEffect(() => {
    inputRef.current?.focus();
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-no-drag
      className="absolute z-50 w-52 bg-popover border border-border rounded-xl node-shadow overflow-hidden"
      style={{ left: position.x, top: position.y }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="p-2 border-b border-border flex items-center gap-2">
        <Search size={13} className="text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search pages..."
          className="flex-1 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
        />
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X size={13} />
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto scrollbar-none p-1">
        {filtered.map(t => (
          <button
            key={t.type}
            onClick={() => onSelect(t.type, t.label)}
            className="w-full text-left px-3 py-1.5 text-xs rounded-lg text-foreground hover:bg-muted transition-colors"
          >
            {t.label}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-3">No results</p>
        )}
      </div>
    </div>
  );
}
